// frontend/src/pages/Tickets/PartnerTickets/PartnerMyTickets.js
import React, { useState, useEffect, useCallback } from 'react';
import { Table, Tag, Space, Button, Input, Typography, Card, Spin, Modal, Tooltip, Dropdown, Menu, Tabs, Select, Form } from 'antd';
import {
  SearchOutlined, DownloadOutlined, EyeOutlined, FilterOutlined,
  CheckCircleOutlined, CloseCircleOutlined, SignatureOutlined, ReloadOutlined
} from '@ant-design/icons';
import moment from 'moment';
import api from '../../../utils/api';
import '../TicketsList.css';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useNotificationApi } from '../../../context/NotificationContext';
import { useAuth } from '../../../context/AuthContext';
import { useContracts } from '../../../hooks/useContracts';

const { Title, Text } = Typography;
const { Search } = Input;
const { TabPane } = Tabs;
const { Option } = Select;

// Helper to get status color
const getStatusColor = (status) => {
  switch (status?.toLowerCase()) {
    case 'unassigned': return 'red';
    case 'open': return 'blue';
    case 'waiting for support': return 'orange';
    case 'in progress': return 'geekblue';
    case 'resolved': return 'green';
    case 'closed': return 'default';
    case 'pending approval': return 'purple';
    case 're-opened': return 'volcano';
    case 'on-hold': return 'magenta';
    case 'cancelled': return 'red';
    default: return 'default';
  }
};

// Helper to get priority color
const getPriorityColor = (priority) => {
  switch (priority?.toLowerCase()) {
    case 'urgent': return 'red';
    case 'high': return 'volcano';
    case 'medium': return 'gold';
    case 'low': return 'lime';
    case 'critical': return 'red';
    default: return 'default';
  }
};

const PartnerMyTickets = () => {
  const [createdTickets, setCreatedTickets] = useState([]);
  const [toSignTickets, setToSignTickets] = useState([]);
  const [loadingCreatedTickets, setLoadingCreatedTickets] = useState(true);
  const [loadingToSignTickets, setLoadingToSignTickets] = useState(true);
  const [createdPagination, setCreatedPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
    showSizeChanger: true,
    pageSizeOptions: ['10', '20', '50', '100'],
  });
  const [toSignPagination, setToSignPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
    showSizeChanger: true,
    pageSizeOptions: ['10', '20', '50', '100'],
  });
  const [globalSearchText, setGlobalSearchText] = useState('');
  const [columnFilters, setColumnFilters] = useState({});
  const [sortInfo, setSortInfo] = useState({});
  const [searchParams] = useSearchParams();
  const initialTab = searchParams.get('tab') === 'to_sign' ? 'to_sign' : 'created';
  const [activeTab, setActiveTab] = useState(initialTab);
  const navigate = useNavigate();
  const notificationApi = useNotificationApi();
  const { user } = useAuth();
  const [appSettings, setAppSettings] = useState({
    dateFormat: 'DD/MM/YYYY',
    timeFormat: 'HH:mm',
    appTitle: 'My Application'
  });

  // Get main contractor's contract IDs for filtering
  const userContractIds = user?.mainContractorContractIds || [];
  const isMainContractor = user?.isMainContractor === true && userContractIds.length > 0;

  // Fetch contract names
  const { contracts: userContracts, loading: loadingContracts, contractMap } = useContracts(userContractIds);
  
  // State for contract names mapping
  const [contractNames, setContractNames] = useState({});

  // Update contract names mapping when contracts are loaded
  useEffect(() => {
    if (userContracts.length > 0) {
      const nameMap = {};
      userContracts.forEach(contract => {
        nameMap[contract.id] = contract.name;
      });
      setContractNames(nameMap);
      console.log('[PARTNER MyTickets] Contract names loaded:', nameMap);
    }
  }, [userContracts]);

  // New state for customer users dropdown and signing modal
  const [customerUsers, setCustomerUsers] = useState([]);
  const [loadingCustomerUsers, setLoadingCustomerUsers] = useState(false);
  const [signModalVisible, setSignModalVisible] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [form] = Form.useForm();

  // Filter tickets by contract (client-side filtering)
  const filterTicketsByContract = useCallback((ticketsToFilter) => {
    if (!isMainContractor || userContractIds.length === 0) {
      return ticketsToFilter;
    }
    
    const filtered = ticketsToFilter.filter(ticket => {
      const ticketContractId = ticket.contractId;
      if (!ticketContractId) return false;
      return userContractIds.includes(ticketContractId);
    });
    
    console.log(`[PARTNER MyTickets] Filtered ${ticketsToFilter.length} tickets down to ${filtered.length} tickets`);
    return filtered;
  }, [isMainContractor, userContractIds]);

  const showError = useCallback((title, content, key = 'globalError') => {
    notificationApi.destroy(key);
    try {
      notificationApi.error({
        key,
        message: title,
        description: content,
        duration: 4.5,
        placement: 'topRight',
        style: {
          width: 400,
          zIndex: 10000,
        },
      });
    } catch (err) {
      console.error("Notification failed, falling back to Modal.error:", err);
      Modal.error({
        title,
        content,
        centered: true,
        zIndex: 99999,
      });
    }
  }, [notificationApi]);

  const showSuccess = useCallback((title, description, key = 'globalSuccess') => {
    notificationApi.success({
      key,
      message: title,
      description: description,
      duration: 3,
      placement: 'topRight',
      style: {
        width: 400,
        zIndex: 10000,
      },
    });
  }, [notificationApi]);

  const showLoading = useCallback((key, messageContent) => {
    let dismissNotification;
    const delay = 300;
    const minDisplayTime = 500;
    const startTime = Date.now();

    const timer = setTimeout(() => {
      dismissNotification = notificationApi.open({
        key,
        message: messageContent,
        duration: 0,
        placement: 'topRight',
        style: {
          width: 400,
          zIndex: 10000,
        },
      });
    }, delay);

    return () => {
      clearTimeout(timer);
      const elapsed = Date.now() - startTime;
      const remaining = Math.max(0, minDisplayTime - elapsed);

      setTimeout(() => {
        if (dismissNotification) {
          typeof dismissNotification === 'function' ? dismissNotification() : notificationApi.destroy(key);
        } else {
          notificationApi.destroy(key);
        }
      }, remaining);
    };
  }, [notificationApi]);

  const fetchAppSettings = useCallback(async () => {
    try {
      const response = await api.get('/api/settings');
      if (response.data && response.data.success) {
        setAppSettings(prev => ({
          ...prev,
          ...response.data.data
        }));
      }
    } catch (error) {
      console.error('Error fetching application settings:', error.response?.data || error.message);
    }
  }, []);

  const fetchTickets = useCallback(async (type = 'created', params = {}, triggeredByAction = false) => {
    if (!user || !user.id) {
      if (type === 'created') setLoadingCreatedTickets(false);
      if (type === 'to_sign') setLoadingToSignTickets(false);
      return;
    }

    const setLoading = type === 'created' ? setLoadingCreatedTickets : setLoadingToSignTickets;
    setLoading(true);
    const loadingKey = `fetch${type}TicketsLoading`;
    const hideLoading = showLoading(loadingKey, `Loading ${type === 'created' ? 'created' : 'to sign'} tickets...`);

    try {
      const response = await api.get('/api/tickets/my-tickets', {
        params: {
          type: type === 'to_sign' ? 'tickets-to-sign' : type,
          search: globalSearchText,
          status: type === 'to_sign' ? 'Closed' : columnFilters?.statusName?.join(','),
          priority: columnFilters?.priorityName?.join(','),
          category: columnFilters?.ticketCategoryName?.join(','),
          sortBy: sortInfo?.field ? 
            `${sortInfo.field}:${sortInfo.order === 'ascend' ? 'ASC' : 'DESC'}` : 
            undefined,
        },
      });

      const responseData = response.data.data || [];
      const totalCount = response.data.totalCount || responseData.length;
      
      // Filter by contract for main contractors
      const filteredData = filterTicketsByContract(responseData);
      const filteredTotal = filteredData.length;

      if (type === 'created') {
        setCreatedTickets(filteredData);
        setCreatedPagination(prev => ({
          ...prev,
          total: filteredTotal,
          current: params.pagination?.current || prev.current,
          pageSize: params.pagination?.pageSize || prev.pageSize,
        }));
      } else {
        setToSignTickets(filteredData);
        setToSignPagination(prev => ({
          ...prev,
          total: filteredTotal,
          current: params.pagination?.current || prev.current,
          pageSize: params.pagination?.pageSize || prev.pageSize,
        }));
      }
      hideLoading();
      if (!triggeredByAction) {
        showSuccess('Tickets Loaded', `${type === 'created' ? 'Created' : 'To Sign'} ticket list fetched successfully!`, `${type}TicketsSuccess`);
      }
    } catch (error) {
      hideLoading();
      console.error(`Error fetching ${type} tickets:`, error.response?.data || error.message, error);
      let errorMessage = `Failed to load ${type === 'created' ? 'created' : 'to sign'} tickets. Please try again.`;
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      }
      showError('Fetch Error', errorMessage, `${type}TicketsError`);
    } finally {
      setLoading(false);
    }
  }, [user, globalSearchText, columnFilters, sortInfo, filterTicketsByContract, showLoading, showSuccess, showError]);

  const fetchCustomerUsers = useCallback(async () => {
    setLoadingCustomerUsers(true);
    try {
      const response = await api.get('/api/users/customers');
      if (response.data.success) {
        setCustomerUsers(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching customer users:', error);
      showError('Fetch Error', 'Failed to load customer users for signing');
    } finally {
      setLoadingCustomerUsers(false);
    }
  }, [showError]);

  const refreshTicketData = async (ticketId) => {
    try {
      const response = await api.get(`/api/tickets/${ticketId}`);
      if (response.data.success) {
        const ticketData = response.data.data;
        // Update the specific ticket in both lists
        setCreatedTickets(prev => prev.map(ticket => 
          ticket.id === ticketId ? ticketData : ticket
        ).filter(ticket => filterTicketsByContract([ticket]).length > 0));
        setToSignTickets(prev => prev.map(ticket => 
          ticket.id === ticketId ? ticketData : ticket
        ).filter(ticket => filterTicketsByContract([ticket]).length > 0));
      }
    } catch (error) {
      console.error('Error refreshing ticket data:', error);
    }
  };

  const handleSign = async (ticketId, nextSignerId = null) => {
    console.log(`[DEBUG] Signing ticket ${ticketId}, nextSignerId: ${nextSignerId}`);
    console.log(`[DEBUG] User role: ${user?.roleName}`);
    const loadingKey = `signTicket_${ticketId}`;
    const hideLoading = showLoading(loadingKey, 'Signing ticket...');

    try {
      const payload = nextSignerId ? { nextSignerId } : {};
      console.log(`[DEBUG] Payload:`, payload);
      const response = await api.post(`/api/tickets/${ticketId}/sign`, payload);
      console.log(`[DEBUG] Sign response:`, response.data);
      
      hideLoading();
      showSuccess('Ticket Signed', 'Ticket has been successfully signed.', `signTicketSuccess_${ticketId}`);
      
      // Refresh BOTH ticket lists to ensure UI is updated
      console.log(`[DEBUG] Refreshing ticket lists...`);
      await fetchTickets('to_sign', {}, true);
      await fetchTickets('created', {}, true);
      
      // Also refresh the specific ticket
      console.log(`[DEBUG] Refreshing individual ticket ${ticketId}...`);
      await refreshTicketData(ticketId);
      
      setSignModalVisible(false);
      setSelectedTicket(null);
      form.resetFields();
      
    } catch (error) {
      hideLoading();
      console.error('Error signing ticket:', error);
      console.error('Error response:', error.response?.data);
      let errorMessage = 'An unexpected error occurred while signing the ticket.';
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      }
      if (error.response?.data?.details) {
        errorMessage += ` Details: ${error.response.data.details}`;
      }
      showError('Sign Error', errorMessage, `signTicketError_${ticketId}`);
    }
  };

  const openSignModal = (ticket) => {
    setSelectedTicket(ticket);
    setSignModalVisible(true);
    fetchCustomerUsers();
  };

  const handleDirectSign = (ticketId) => {
    handleSign(ticketId);
  };

  const handleModalSign = () => {
    form.validateFields().then(values => {
      if (selectedTicket && values.customerUser) {
        handleSign(selectedTicket.id, values.customerUser);
      }
    });
  };

  const needsCustomerSelection = () => {
    const userRole = user?.roleName;
    return [
      'Internal Staff Supervisor',
      'Partner Supervisor',
      'System Admin'
    ].includes(userRole);
  };

  const handleManualRefresh = () => {
    fetchTickets('created');
    fetchTickets('to_sign');
    showSuccess('Refreshed', 'Ticket lists refreshed successfully.');
  };

  useEffect(() => {
    if (user) {
      fetchTickets('created');
      fetchTickets('to_sign');
      fetchAppSettings();
    }
  }, [user, fetchTickets, fetchAppSettings]);

  const handleTableChange = (pagination, filters, sorter, extra, type = 'created') => {
    const newFilters = {
      ...filters,
      statusName: type === 'to_sign' ? ['Closed'] : filters.statusName || [],
      priorityName: filters.priorityName || [],
      ticketCategoryName: filters.ticketCategoryName || []
    };
    setColumnFilters(newFilters);
    setSortInfo(sorter || {});
    fetchTickets(type, { pagination, columnFilters: newFilters, sortInfo: sorter }, true);
  };

  const handleGlobalSearch = useCallback((value) => {
    setGlobalSearchText(value);
    fetchTickets(activeTab, { globalSearchText: value }, true);
  }, [activeTab, fetchTickets]);

  const handleViewTicket = useCallback((ticketId) => {
    navigate(`/tickets/${ticketId}`);
  }, [navigate]);

  const handleDownload = async (ticketUuid, format) => {
    const loadingKey = `download${format.toUpperCase()}Loading`;
    const hideLoading = showLoading(loadingKey, `Generating ${format.toUpperCase()}...`);

    try {
      const response = await api.get(`/api/reports/tickets/${ticketUuid}/borang-aduan`, {
        params: { format },
        responseType: 'blob',
        timeout: 60000,
      });

      const contentDisposition = response.headers['content-disposition'];
      let filename = `BorangAduan_${ticketUuid}.${format}`;
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename\*?=['"]?(?:UTF-8''|\w*')?([^"'\r\n]+)['"]?/);
        if (filenameMatch && filenameMatch[1]) {
          filename = decodeURIComponent(filenameMatch[1]);
        }
      }

      const contentType = format === 'pdf' ? 'application/pdf' : 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
      const url = window.URL.createObjectURL(new Blob([response.data], { type: contentType }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);

      hideLoading();
      showSuccess(
        `${format.toUpperCase()} Generated`,
        `Borang Aduan for ticket ${ticketUuid} downloaded successfully!`,
        `download${format.toUpperCase()}Success`
      );
    } catch (error) {
      hideLoading();
      console.error(`Error downloading ${format.toUpperCase()}:`, error.response?.data || error.message, error);
      let errorMessage = `An unexpected error occurred while generating the ${format.toUpperCase()}.`;
      if (error.response && error.response.data instanceof Blob) {
        const errorText = await error.response.data.text();
        try {
          const errorJson = JSON.parse(errorText);
          errorMessage = errorJson.message || errorMessage;
        } catch (parseError) {
          errorMessage = errorText || errorMessage;
        }
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      }

      showError(
        `Failed to Download ${format.toUpperCase()}`,
        errorMessage,
        `download${format.toUpperCase()}Error`
      );
    }
  };

  const handleTabChange = (key) => {
    setActiveTab(key);
    setGlobalSearchText('');
    setColumnFilters({});
    setSortInfo({});
    fetchTickets(key);
  };

  const getColumnSearchProps = (dataIndex, placeholderText) => ({
    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
      <div style={{ padding: 8 }}>
        <Input
          placeholder={`Search ${placeholderText}`}
          value={selectedKeys[0]}
          onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
          onPressEnter={() => confirm()}
          style={{ marginBottom: 8, display: 'block' }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() => confirm()}
            icon={<SearchOutlined />}
            size="small"
            style={{ width: 90 }}
          >
            Search
          </Button>
          <Button onClick={() => clearFilters()} size="small" style={{ width: 90 }}>
            Reset
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered) => (
      <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />
    ),
    onFilter: (value, record) =>
      record[dataIndex] ? record[dataIndex].toString().toLowerCase().includes(value.toLowerCase()) : '',
  });

  // Base columns with CONTRACT column added
  const baseColumns = [
    {
      title: 'Number',
      dataIndex: 'ticketUuid',
      key: 'ticketUuid',
      render: (text, record) => (
        <Button
          type="link"
          onClick={() => handleViewTicket(record.id)}
          style={{ padding: 0 }}
        >
          {text}
        </Button>
      ),
      ...getColumnSearchProps('ticketUuid', 'Number'),
      sorter: true,
      fixed: 'left',
      width: 180,
    },
    {
      title: 'Contract',
      dataIndex: 'contractId',
      key: 'contract',
      render: (contractId) => (
        <Tag color="blue" style={{ fontSize: '12px', fontWeight: 500 }}>
          {contractNames[contractId] || `Contract ${contractId}`}
        </Tag>
      ),
      width: 200,
    },
    {
      title: 'Summary',
      dataIndex: 'title',
      key: 'summary',
      ...getColumnSearchProps('title', 'Summary'),
      sorter: true,
      ellipsis: true,
      width: 300,
    },
    {
      title: 'Reporter',
      dataIndex: 'creatorFirstName',
      key: 'reporter',
      render: (text, record) => `${record.creatorFirstName || ''} ${record.creatorLastName || ''}`,
      ...getColumnSearchProps('creatorFirstName', 'Reporter'),
      sorter: true,
      width: 180,
    },
    {
      title: 'Assignee',
      dataIndex: 'assigneeFirstName',
      key: 'assignee',
      render: (text, record) => record.assigneeFirstName ? `${record.assigneeFirstName || ''} ${record.assigneeLastName || ''}` : 'Unassigned',
      ...getColumnSearchProps('assigneeFirstName', 'Assignee'),
      sorter: true,
      width: 180,
    },
    {
      title: 'Status',
      dataIndex: 'statusName',
      key: 'statusName',
      filters: [
        { text: 'Unassigned', value: 'Unassigned' },
        { text: 'Open', value: 'Open' },
        { text: 'Waiting for Support', value: 'Waiting for Support' },
        { text: 'In Progress', value: 'In Progress' },
        { text: 'Resolved', value: 'Resolved' },
        { text: 'Closed', value: 'Closed' },
        { text: 'Pending Approval', value: 'Pending Approval' },
        { text: 'Re-opened', value: 'Re-opened' },
        { text: 'On-Hold', value: 'On-Hold' },
        { text: 'Cancelled', value: 'Cancelled' },
      ],
      onFilter: (value, record) => record.statusName?.toLowerCase().includes(value.toLowerCase()),
      render: (status) => (
        <Tag color={getStatusColor(status)} key={status}>
          {status?.toUpperCase()}
        </Tag>
      ),
      width: 180,
    },
    {
      title: 'Priority',
      dataIndex: 'priorityName',
      key: 'priorityName',
      filters: [
        { text: 'High', value: 'High' },
        { text: 'Medium', value: 'Medium' },
        { text: 'Low', value: 'Low' },
      ],
      onFilter: (value, record) => record.priorityName?.toLowerCase().includes(value.toLowerCase()),
      render: (priority) => (
        <Tag color={getPriorityColor(priority)} key={priority}>
          {priority?.toUpperCase()}
        </Tag>
      ),
      width: 120,
    },
    {
      title: 'Reported Date',
      dataIndex: 'caseReportedDate',
      key: 'caseReportedDate',
      sorter: true,
      render: (text) => text ? moment(text).format(`${appSettings.dateFormat} ${appSettings.timeFormat}`) : 'N/A',
      width: 180,
    },
  ];

  const createdColumns = [
    ...baseColumns,
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space size="middle">
          <Button type="link" icon={<EyeOutlined />} onClick={() => handleViewTicket(record.id)}>
            View
          </Button>
          <Dropdown
            menu={
              <Menu
                onClick={({ key }) => handleDownload(record.ticketUuid, key)}
                items={[
                  {
                    key: 'pdf',
                    label: 'Download as PDF',
                    icon: <DownloadOutlined />,
                  },
                  {
                    key: 'docx',
                    label: 'Download as Word',
                    icon: <DownloadOutlined />,
                  },
                ]}
              />
            }
            trigger={['click']}
          >
            <Button type="link" icon={<DownloadOutlined />}>
              Download
            </Button>
          </Dropdown>
        </Space>
      ),
      width: 180,
      fixed: 'right',
    },
  ];

  const toSignColumns = [
    {
      title: 'Number',
      dataIndex: 'ticketUuid',
      key: 'ticketUuid',
      render: (text, record) => (
        <Button
          type="link"
          onClick={() => handleViewTicket(record.id)}
          style={{ padding: 0 }}
        >
          {text}
        </Button>
      ),
      ...getColumnSearchProps('ticketUuid', 'Number'),
      sorter: true,
      fixed: 'left',
      width: 180,
    },
    {
      title: 'Contract',
      dataIndex: 'contractId',
      key: 'contract',
      render: (contractId) => (
        <Tag color="blue" style={{ fontSize: '12px', fontWeight: 500 }}>
          {contractNames[contractId] || `Contract ${contractId}`}
        </Tag>
      ),
      width: 200,
    },
    {
      title: 'Summary',
      dataIndex: 'title',
      key: 'summary',
      ...getColumnSearchProps('title', 'Summary'),
      sorter: true,
      ellipsis: true,
      width: 300,
    },
    {
      title: 'Reporter',
      dataIndex: 'creatorFirstName',
      key: 'reporter',
      render: (text, record) => `${record.creatorFirstName || ''} ${record.creatorLastName || ''}`,
      ...getColumnSearchProps('creatorFirstName', 'Reporter'),
      sorter: true,
      width: 180,
    },
    {
      title: 'Assignee',
      dataIndex: 'assigneeFirstName',
      key: 'assignee',
      render: (text, record) => record.assigneeFirstName ? `${record.assigneeFirstName || ''} ${record.assigneeLastName || ''}` : 'Unassigned',
      ...getColumnSearchProps('assigneeFirstName', 'Assignee'),
      sorter: true,
      width: 180,
    },
    {
      title: 'Status',
      dataIndex: 'statusName',
      key: 'statusName',
      render: (status) => (
        <Tag color={getStatusColor(status)} key={status}>
          {status?.toUpperCase()}
        </Tag>
      ),
      width: 180,
    },
    {
      title: 'Priority',
      dataIndex: 'priorityName',
      key: 'priorityName',
      filters: [
        { text: 'High', value: 'High' },
        { text: 'Medium', value: 'Medium' },
        { text: 'Low', value: 'Low' },
      ],
      onFilter: (value, record) => record.priorityName?.toLowerCase().includes(value.toLowerCase()),
      render: (priority) => (
        <Tag color={getPriorityColor(priority)} key={priority}>
          {priority?.toUpperCase()}
        </Tag>
      ),
      width: 120,
    },
    {
      title: 'Reported Date',
      dataIndex: 'caseReportedDate',
      key: 'caseReportedDate',
      sorter: true,
      render: (text) => text ? moment(text).format(`${appSettings.dateFormat} ${appSettings.timeFormat}`) : 'N/A',
      width: 180,
    },
    {
      title: 'Supervisor Signed',
      dataIndex: 'supervisorSignedAt',
      key: 'supervisorSignedAt',
      render: (text) => text ? (
        <Tag color="green" icon={<CheckCircleOutlined />}>SIGNED</Tag>
      ) : (
        <Tag color="red" icon={<CloseCircleOutlined />}>PENDING</Tag>
      ),
      width: 150,
    },
    {
      title: 'Customer Signed',
      dataIndex: 'customerStandardSignedAt',
      key: 'customerStandardSignedAt',
      render: (text) => text ? (
        <Tag color="green" icon={<CheckCircleOutlined />}>SIGNED</Tag>
      ) : (
        <Tag color="red" icon={<CloseCircleOutlined />}>PENDING</Tag>
      ),
      width: 150,
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space size="middle">
          <Button type="link" icon={<EyeOutlined />} onClick={() => handleViewTicket(record.id)}>
            View
          </Button>
          <Dropdown
            menu={
              <Menu
                onClick={({ key }) => handleDownload(record.ticketUuid, key)}
                items={[
                  { key: 'pdf', label: 'Download as PDF', icon: <DownloadOutlined /> },
                  { key: 'docx', label: 'Download as Word', icon: <DownloadOutlined /> },
                ]}
              />
            }
            trigger={['click']}
          >
            <Button type="link" icon={<DownloadOutlined />}>
              Download
            </Button>
          </Dropdown>
          <Button 
            type="link" 
            icon={<SignatureOutlined />} 
            onClick={() => needsCustomerSelection() ? openSignModal(record) : handleDirectSign(record.id)}
          >
            Sign
          </Button>
        </Space>
      ),
      width: 220,
      fixed: 'right',
    },
  ];

  // Show access restricted message if not main contractor
  if (!isMainContractor) {
    return (
      <Card className="tickets-list-page-card">
        <div style={{ textAlign: 'center', padding: '50px' }}>
          <Title level={3}>Access Restricted</Title>
          <Text type="secondary">This page is only for main contractors with assigned contracts.</Text>
        </div>
      </Card>
    );
  }

  if (isMainContractor && userContractIds.length === 0) {
    return (
      <Card className="tickets-list-page-card">
        <div style={{ textAlign: 'center', padding: '50px' }}>
          <Title level={3}>No Contracts Assigned</Title>
          <Text type="secondary">You don't have any contracts assigned. Please contact your administrator.</Text>
        </div>
      </Card>
    );
  }

  return (
    <Card className="tickets-list-page-card">
      <div className="tickets-list-header">
        <Title level={2} className="tickets-list-title">Partner My Tickets</Title>
        <Text type="secondary" className="tickets-list-description">
          {loadingContracts ? 'Loading contract information...' : 
            userContracts.length > 0 ? `Viewing tickets for: ${userContracts.map(c => c.name).join(', ')}` : 
            'No contracts assigned'}
        </Text>
      </div>

      <Modal
        title="Select Customer User for Next Signature"
        open={signModalVisible}
        onCancel={() => {
          setSignModalVisible(false);
          setSelectedTicket(null);
          form.resetFields();
        }}
        onOk={handleModalSign}
        okText="Sign and Forward to Customer"
        cancelText="Cancel"
        width={600}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="customerUser"
            label="Select Customer User"
            rules={[{ required: true, message: 'Please select a customer user to forward the ticket to' }]}
          >
            <Select
              placeholder="Search and select a customer user..."
              loading={loadingCustomerUsers}
              showSearch
              optionFilterProp="children"
              filterOption={(input, option) =>
                option.children.toLowerCase().includes(input.toLowerCase())
              }
              notFoundContent={loadingCustomerUsers ? <Spin size="small" /> : "No customer users found"}
            >
              {customerUsers.map(user => (
                <Option key={user.id} value={user.id}>
                  {`${user.firstName} ${user.lastName} - ${user.organizationName || 'No Organization'} (${user.roleName})`}
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Text type="secondary">
            After you sign, the ticket will be forwarded to the selected customer user for their signature.
          </Text>
        </Form>
      </Modal>

      <Tabs 
        activeKey={activeTab} 
        onChange={handleTabChange} 
        className="my-tickets-tabs"
      >
        <TabPane tab="Ticket(s) Created" key="created">
          <div className="tickets-table-filters-container mb-4">
            <Search
              placeholder="Search by ticket number, summary, reporter, or assignee..."
              enterButton
              value={globalSearchText}
              onChange={(e) => setGlobalSearchText(e.target.value)}
              onSearch={handleGlobalSearch}
              style={{ width: 400 }}
              className="tickets-global-search"
            />
            <Button 
              icon={<ReloadOutlined />} 
              onClick={handleManualRefresh}
              style={{ marginLeft: 8 }}
            >
              Refresh
            </Button>
          </div>

          <div className="tickets-table-container">
            <Table
              columns={createdColumns}
              dataSource={createdTickets}
              rowKey="id"
              pagination={createdPagination}
              loading={loadingCreatedTickets}
              onChange={(pagination, filters, sorter, extra) => handleTableChange(pagination, filters, sorter, extra, 'created')}
              scroll={{ x: 'max-content' }}
              className="tickets-ant-table"
            />
          </div>
        </TabPane>
        <TabPane tab="Tickets To Sign" key="to_sign">
          <div className="tickets-table-filters-container mb-4">
            <Search
              placeholder="Search by ticket number, summary, reporter, or assignee..."
              enterButton
              value={globalSearchText}
              onChange={(e) => setGlobalSearchText(e.target.value)}
              onSearch={handleGlobalSearch}
              style={{ width: 400 }}
              className="tickets-global-search"
            />
            <Button 
              icon={<ReloadOutlined />} 
              onClick={handleManualRefresh}
              style={{ marginLeft: 8 }}
            >
              Refresh
            </Button>
          </div>

          <div className="tickets-table-container">
            <Table
              columns={toSignColumns}
              dataSource={toSignTickets}
              rowKey="id"
              pagination={toSignPagination}
              loading={loadingToSignTickets}
              onChange={(pagination, filters, sorter, extra) => handleTableChange(pagination, filters, sorter, extra, 'to_sign')}
              scroll={{ x: 'max-content' }}
              className="tickets-ant-table"
            />
          </div>
        </TabPane>
      </Tabs>
    </Card>
  );
};

export default PartnerMyTickets;